import { useEffect, useCallback } from 'react';

// styles
import styles from './media-overlay.module.scss';

// utils
import { useOnEscapePress } from 'features/header/components/header/mega-menu/hooks/use-key-press';

export function useFocusTrap(onClose: () => void) {
  useOnEscapePress(onClose);

  const handleTabKeyPress = useCallback((e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;

    const overlay = document.querySelector(`.${styles.overlayWrapper}`) as HTMLElement;
    if (!overlay) return;

    const focusableElements = overlay.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    ) as NodeListOf<HTMLElement>;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    }
  }, []);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const allElements = document.querySelectorAll('body > *:not([role="dialog"])');
    const originalAriaStates = Array.from(allElements).map((el) => ({
      element: el,
      original: el.getAttribute('aria-hidden'),
    }));

    allElements.forEach((el) => el.setAttribute('aria-hidden', 'true'));

    const closeButton = document.querySelector(`.${styles.overlayCloseButton}`) as HTMLElement;
    closeButton?.focus();

    document.addEventListener('keydown', handleTabKeyPress);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', handleTabKeyPress);

      originalAriaStates.forEach(({ element, original }) => {
        if (original === null) {
          element.removeAttribute('aria-hidden');
        } else {
          element.setAttribute('aria-hidden', original);
        }
      });
    };
  }, [handleTabKeyPress]);
}
